// AboutCompanyPage.jsx
import React, { useMemo, useState, useEffect, Fragment } from "react";
import "./Styles.css";
import HeaderToMainPage from "../Universal/Header/Index";
import Footer from "../Universal/Footer/Index";
import JumbotronHero from "../Universal/HeroJumbo/Index";
import { useWindowWidth } from "@react-hook/window-size";
 
const AboutCompanyHelper = () => {
  const sections = useMemo(
    () => [
      { id: "origin", label: "The Origin" },
      { id: "development", label: "Development Journey" },
      { id: "philosophy", label: "Architecture Philosophy" },
      { id: "difference", label: "Differentiation" },
      { id: "founder", label: "Founder Perspective" },
      { id: "maturity", label: "Platform Maturity" },
      { id: "vision", label: "Long-Term Vision" },
    ],
    []
  );

  const [activeId, setActiveId] = useState("origin");

  useEffect(() => {
    const onScroll = () => {
      const topOffset = 120;
      const ids = sections.map((s) => s.id);
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top - topOffset <= 0) current = id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };  
  const width = useWindowWidth();
  return (
    <Fragment>
      <HeaderToMainPage />
      {width <= 775 ? <div style={{ height: 87.25 }} /> : null}
      <JumbotronHero
        kicker="COMPANY PROFILE • INFRASTRUCTURE ORIGIN"
        title={
          <>
            Built from <span className="jh-accent">Infrastructure</span> — 
            Not <span className="jh-accent2">Marketing</span>.
          </>
        }
        subtitle="Ethosino was engineered from first principles to address structural weaknesses in traditional operator infrastructure. The platform evolved through multi-year architectural refinement, tenant isolation modeling, deterministic verification systems, and governance-first design."
        primaryCta={{ label: "Review Technical Architecture", href: "/info/multi-tenant/infrastructure" }}
        secondaryCta={{ label: "Book a Live Demo", href: "/book-demo" }}
        checklistTitle="Platform Evolution Milestones"
        checklist={[
          "Ground-up core engine architecture development",
          "Transition from single-tenant to full multi-tenant isolation",
          "Deterministic provably fair seed lifecycle implementation",
          "Replay reconstruction & audit artifact framework",
          "Operator-scoped governance & ruleset versioning controls",
          "Ledger modeling with exportable compliance structures",
          "Administrative boundary hardening & role separation",
          "Modular white-label provisioning infrastructure"
        ]}
        bgImage="/assets/images/hero/shake.png"
      />
      <main className="ac-page">
          {/* Decorative background orbs + grid */}
          <div className="ac-bg" aria-hidden="true">
            <div className="ac-orb ac-orb--a" />
            <div className="ac-orb ac-orb--b" />
            <div className="ac-orb ac-orb--c" />
            <div className="ac-grid" />
            <div className="ac-noise" />
          </div>

          {/* Hero */}
          <header className="ac-hero">
            <div className="ac-container">
              <div className="ac-hero-grid">
                <div className="ac-hero-left">
                  <div className="ac-kicker">
                    COMPANY • INFRASTRUCTURE PROFILE • DUE-DILIGENCE READY
                  </div>

                  <h1 className="ac-hero-title">
                    Built from <span className="ac-accent">Infrastructure</span> — Not{" "}
                    <span className="ac-accent2">Marketing</span>.
                  </h1>

                  <div className="ac-hero-underline" />

                  <p className="ac-hero-subtitle">
                    Ethosino was engineered from first principles to solve structural
                    problems in operator infrastructure — not to launch another surface-level
                    gaming brand.
                  </p>

                  <p className="ac-hero-body">
                    The platform was built ground-up as a multi-tenant system with deterministic
                    verification, tenant-scoped governance, and exportable audit trails. The
                    goal is simple: give operators and technical reviewers an infrastructure
                    environment whose behavior can be demonstrated, inspected, and defended.
                    That means clear boundaries between tenants, transparent verification
                    artifacts for game outcomes, governed rulesets, and an operator control
                    surface built for operational accountability rather than presentation.
                  </p>

                  <div className="ac-hero-cta">
                    <a className="ac-btn ac-btn--primary" href="/book-demo">
                      Book a Live Demo <span aria-hidden="true">→</span>
                    </a>
                    <a className="ac-btn ac-btn--ghost" href="/architecture.pdf">
                      Review Technical Architecture
                    </a>
                  </div>

                  <div className="ac-hero-meta">
                    <div className="ac-meta-pill">
                      <span className="ac-dot ac-dot--cyan" />
                      Multi-tenant provisioning model
                    </div>
                    <div className="ac-meta-pill">
                      <span className="ac-dot ac-dot--green" />
                      Deterministic verification artifacts
                    </div>
                    <div className="ac-meta-pill">
                      <span className="ac-dot ac-dot--violet" />
                      Governed rulesets & exportable logs
                    </div>
                  </div>
                </div>

                <aside className="ac-hero-right">
                  <div className="ac-panel ac-panel--milestones">
                    <div className="ac-panel-top">
                      <h2 className="ac-panel-title">Platform Milestones</h2>
                      <p className="ac-panel-sub">
                        High-level progression of the build, summarized for reviewers.
                      </p>
                    </div>

                    <ol className="ac-milestones">
                      <li className="ac-milestone">
                        <div className="ac-milestone-badge">Year 1</div>
                        <div className="ac-milestone-content">
                          <div className="ac-milestone-title">Core engine architecture</div>
                          <div className="ac-milestone-desc">
                            Foundational game execution model, event logging strategy, and
                            operator-grade controls, with a bias toward deterministic behavior
                            and replayability.
                          </div>
                        </div>
                      </li>
                      <li className="ac-milestone">
                        <div className="ac-milestone-badge">Year 2</div>
                        <div className="ac-milestone-content">
                          <div className="ac-milestone-title">Multi-tenant isolation model</div>
                          <div className="ac-milestone-desc">
                            Tenant boundaries formalized: configuration isolation, scoped
                            permissions, and per-tenant policy surfaces designed for multiple
                            operators within a single infrastructure deployment.
                          </div>
                        </div>
                      </li>
                      <li className="ac-milestone">
                        <div className="ac-milestone-badge">Year 3</div>
                        <div className="ac-milestone-content">
                          <div className="ac-milestone-title">Provably fair + replay verification</div>
                          <div className="ac-milestone-desc">
                            Deterministic verification artifacts, replay tooling, and an
                            audit-grade approach to outcome reconstruction for dispute
                            resolution and technical review.
                          </div>
                        </div>
                      </li>
                      <li className="ac-milestone">
                        <div className="ac-milestone-badge">Ongoing</div>
                        <div className="ac-milestone-content">
                          <div className="ac-milestone-title">White-label operator controls</div>
                          <div className="ac-milestone-desc">
                            Branding, access controls, tenant-scoped catalog policy, and
                            operational governance tooling that prioritizes accountability.
                          </div>
                        </div>
                      </li>
                      <li className="ac-milestone">
                        <div className="ac-milestone-badge">Ongoing</div>
                        <div className="ac-milestone-content">
                          <div className="ac-milestone-title">Compliance export systems</div>
                          <div className="ac-milestone-desc">
                            Exportable logs, verification artifacts, and structured evidence
                            bundles intended to make system behavior inspectable under
                            due-diligence scrutiny.
                          </div>
                        </div>
                      </li>
                    </ol>

                    <div className="ac-panel-footer">
                      <a className="ac-inline-link" href="/provably-fair-engine">
                        View provably fair architecture <span aria-hidden="true">→</span>
                      </a>
                      <span className="ac-sep" aria-hidden="true" />
                      <a className="ac-inline-link" href="/info/multi-tenant/infrastructure">
                        View multi-tenant model <span aria-hidden="true">→</span>
                      </a>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </header>

          {/* Sticky subnav */}
          <nav className="ac-subnav" aria-label="About Ethosino sections">
            <div className="ac-container">
              <div className="ac-subnav-inner">
                <div className="ac-subnav-label">Navigate</div>
                <ul className="ac-subnav-links">
                  {sections.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        className={`ac-subnav-link ${activeId === s.id ? "is-active" : ""}`}
                        onClick={() => scrollTo(s.id)}
                      >
                        <span className="jh-accent2">{s.label}</span>
                        <span className="ac-subnav-underline" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="ac-subnav-cta">
                  <a className="ac-subnav-pill" href="/book-demo">
                    Request Demo
                  </a>
                </div>
              </div>
            </div>
          </nav>

          {/* Content */}
          <section className="ac-section ac-origin" id="origin">
            <div className="ac-container">
              <div className="ac-section-head">
                <h2 className="ac-h2">
                  <span className="mt-h2-accent">The Origin</span> <span className="ac-h2-underline" aria-hidden="true" />
                </h2>
                <p className="ac-lede">
                  Ethosino started as a practical response to infrastructure gaps that appear
                  repeatedly in operator environments: opaque outcome generation, shallow
                  governance tooling, and limited tenant-scoped accountability.
                </p>
              </div>

              <div className="ac-two-col">
                <div className="ac-col">
                  <p className="ac-p">
                    Traditional “white-label” ecosystems can be effective for speed, but the
                    trade-offs are not always visible until an operator reaches a point where
                    technical scrutiny matters: investor diligence, partner reviews, dispute
                    resolution, internal security audits, or compliance advisory work. In many
                    environments, critical behavior is hidden behind third-party systems that
                    cannot be meaningfully validated by the operator. That makes governance and
                    accountability difficult, even if the brand layer is polished.
                  </p>

                  <p className="ac-p">
                    The original goal behind Ethosino was to build a system where the “how”
                    remains visible. Not as a marketing feature, but as an engineering requirement.
                    That meant: deterministic verification artifacts, tenant isolation that scales
                    beyond basic configuration flags, ruleset versioning so that operational changes
                    are governed rather than implicit, and exportable evidence bundles that support
                    review without requiring privileged access to internal components.
                  </p>

                  <p className="ac-p">
                    The design direction was shaped by a single constraint: if a reviewer asks,
                    “Prove what happened,” the platform should be able to answer without ambiguity.
                    If an operator asks, “Who changed this configuration, and why?” the platform
                    should be able to show a durable audit trail. If a partner asks, “How do tenant
                    boundaries prevent cross-operator exposure?” the platform should provide a
                    coherent isolation model with scoped controls and verifiable behavior.
                  </p>

                  <div className="ac-callout ac-callout--note">
                    <div className="ac-callout-title">Scope note</div>
                    <div className="ac-callout-body">
                      Ethosino is infrastructure software. Operators remain responsible for
                      jurisdictional compliance, legal review, and operational policy decisions.
                      The platform focuses on controlled execution, tenant-scoped governance, and
                      audit-grade traceability.
                    </div>
                  </div>
                </div>

                <aside className="ac-col">
                  <div className="ac-panel ac-panel--gaps">
                    <h3 className="ac-h3">Industry Gaps Identified</h3>
                    <p className="ac-muted">
                      These were recurring patterns observed across operator workflows.
                    </p>

                    <ul className="ac-gap-list">
                      <li>
                        <span className="ac-ic ac-ic--warn" aria-hidden="true">
                          ⦿
                        </span>
                        <div>
                          <div className="ac-gap-title">Opaque fairness systems</div>
                          <div className="ac-gap-desc">
                            Outcome logic is often a black box. Operators may be unable to reproduce
                            results from verifiable inputs or produce clean proof artifacts during disputes.
                          </div>
                        </div>
                      </li>
                      <li>
                        <span className="ac-ic ac-ic--warn" aria-hidden="true">
                          ⦿
                        </span>
                        <div>
                          <div className="ac-gap-title">No deterministic replay reconstruction</div>
                          <div className="ac-gap-desc">
                            Without replay tooling, “what happened” becomes a matter of trust rather than
                            demonstrable computation tied to recorded inputs and versioned rulesets.
                          </div>
                        </div>
                      </li>
                      <li>
                        <span className="ac-ic ac-ic--warn" aria-hidden="true">
                          ⦿
                        </span>
                        <div>
                          <div className="ac-gap-title">Limited tenant-level governance</div>
                          <div className="ac-gap-desc">
                            Multi-operator deployments frequently share core components without a clear
                            governance boundary, increasing operational risk for tenant separation and accountability.
                          </div>
                        </div>
                      </li>
                      <li>
                        <span className="ac-ic ac-ic--warn" aria-hidden="true">
                          ⦿
                        </span>
                        <div>
                          <div className="ac-gap-title">Fragile integration contracts</div>
                          <div className="ac-gap-desc">
                            Integrations can be vendor-locked or ad hoc, making future migrations expensive and
                            complicating security posture and incident response.
                          </div>
                        </div>
                      </li>
                      <li>
                        <span className="ac-ic ac-ic--warn" aria-hidden="true">
                          ⦿
                        </span>
                        <div>
                          <div className="ac-gap-title">Weak audit export structures</div>
                          <div className="ac-gap-desc">
                            Logs exist, but they are often not organized into evidence-grade artifacts. That raises
                            friction when operators need to support diligence, compliance advisory review, or internal controls.
                          </div>
                        </div>
                      </li>
                    </ul>

                    <div className="ac-panel-divider" />

                    <div className="ac-mini-metric">
                      <div className="ac-mini-metric-label">Design response</div>
                      <div className="ac-mini-metric-value">
                        Audit-first governance + deterministic verification
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          <section className="ac-section ac-development" id="development">
            <div className="ac-container">
              <div className="ac-section-head">
                <h2 className="ac-h2">
                  <span className="mt-h2-accent">Development Journey</span> <span className="ac-h2-underline" aria-hidden="true" />
                </h2>
                <p className="ac-lede">
                  Ethosino’s build process was shaped by repeated iteration cycles: modeling core
                  systems, stress testing boundaries, refactoring governance surfaces, and enforcing
                  deterministic verifiability as a first-class requirement.
                </p>
              </div>

              <div className="ac-timeline-grid">
                <article className="ac-phase">
                  <div className="ac-phase-top">
                    <div className="ac-phase-tag ac-phase-tag--cyan">Phase 1</div>
                    <h3 className="ac-h3">Engine foundations</h3>
                  </div>
                  <p className="ac-p">
                    Early work focused on a consistent execution model that could support multiple game types
                    while preserving repeatability. Instead of treating outcomes as “results,” the platform models
                    outcomes as deterministic computations derived from explicit inputs and versioned rulesets.
                  </p>
                  <ul className="ac-bullets">
                    <li>Initial event log schema design and normalization</li>
                    <li>Ruleset identifiers + version fields integrated into logs</li>
                    <li>Outcome computation isolated behind testable interfaces</li>
                    <li>Early admin control surface focused on visibility rather than aesthetics</li>
                  </ul>
                  <div className="ac-lessons">
                    <div className="ac-lessons-label">Lesson learned</div>
                    <div className="ac-lessons-body">
                      Outcome generation must be treated like an auditable computation, not like an opaque
                      function call.
                    </div>
                  </div>
                </article>

                <article className="ac-phase">
                  <div className="ac-phase-top">
                    <div className="ac-phase-tag ac-phase-tag--green">Phase 2</div>
                    <h3 className="ac-h3">Multi-tenant restructuring</h3>
                  </div>
                  <p className="ac-p">
                    The platform expanded from a single-tenant concept into a tenant-scoped architecture. This
                    phase emphasized boundaries: scoped configs, tenant identifiers across logs, permission matrices,
                    and policy enforcement that can vary per operator without cross-tenant bleed.
                  </p>
                  <ul className="ac-bullets">
                    <li>Tenant identifiers integrated into all critical entities</li>
                    <li>Per-tenant catalog policy and access enforcement patterns</li>
                    <li>Tenant-scoped admin roles, permissions, and audit logging</li>
                    <li>Isolation strategy for configuration + reporting exports</li>
                  </ul>
                  <div className="ac-lessons">
                    <div className="ac-lessons-label">Lesson learned</div>
                    <div className="ac-lessons-body">
                      Multi-tenant is not a UI feature. The boundary must be enforceable at the data and policy layers.
                    </div>
                  </div>
                </article>

                <article className="ac-phase">
                  <div className="ac-phase-top">
                    <div className="ac-phase-tag ac-phase-tag--violet">Phase 3</div>
                    <h3 className="ac-h3">Provably fair & replay tooling</h3>
                  </div>
                  <p className="ac-p">
                    Deterministic verification moved from a conceptual requirement to a platform capability:
                    seed lifecycles, commitment hashes, nonce strategies, and replay reconstruction workflows.
                    This work also refined how artifacts are packaged for review and export.
                  </p>
                  <ul className="ac-bullets">
                    <li>Seed commitment models integrated into event records</li>
                    <li>Replay artifacts designed for deterministic outcome reconstruction</li>
                    <li>Tenant-scoped seed vault boundaries and access controls</li>
                    <li>Verifier UX concept for “two-click replay” workflows</li>
                  </ul>
                  <div className="ac-lessons">
                    <div className="ac-lessons-label">Lesson learned</div>
                    <div className="ac-lessons-body">
                      Proof is a product surface. It must be comprehensible, exportable, and consistently formed.
                    </div>
                  </div>
                </article>

                <article className="ac-phase">
                  <div className="ac-phase-top">
                    <div className="ac-phase-tag ac-phase-tag--cyan">Phase 4</div>
                    <h3 className="ac-h3">Ledger modeling & dispute posture</h3>
                  </div>
                  <p className="ac-p">
                    The accounting and transaction layer matured into a structured ledger model. The focus was to
                    make financial events traceable, exportable, and consistent with gameplay artifacts. This phase
                    also emphasized dispute workflows: linking outcomes, rulesets, and ledger references.
                  </p>
                  <ul className="ac-bullets">
                    <li>Ledger references linked to gameplay rounds and artifacts</li>
                    <li>Event integrity patterns: immutable logs + append-only records</li>
                    <li>Operator export bundles for financial + fairness records</li>
                    <li>Dispute “evidence packaging” patterns for internal review</li>
                  </ul>
                  <div className="ac-lessons">
                    <div className="ac-lessons-label">Lesson learned</div>
                    <div className="ac-lessons-body">
                      Logs only matter if they map cleanly to user-visible outcomes and reconciled ledger events.
                    </div>
                  </div>
                </article>

                <article className="ac-phase">
                  <div className="ac-phase-top">
                    <div className="ac-phase-tag ac-phase-tag--green">Phase 5</div>
                    <h3 className="ac-h3">Operational controls & risk instrumentation</h3>
                  </div>
                  <p className="ac-p">
                    The platform evolved toward operational maturity: risk controls, tenant policy enforcement, and
                    instrumentation designed for auditability and governance. The approach is not “prevent everything,”
                    but “measure and control with explicit policy and traceable decisions.”
                  </p>
                  <ul className="ac-bullets">
                    <li>Tenant-scoped policy enforcement (limits, access, ruleset gating)</li>
                    <li>Role separation and admin audit log visibility</li>
                    <li>Exportable incident traces for review workflows</li>
                    <li>Guardrails designed for operator governance posture</li>
                  </ul>
                  <div className="ac-lessons">
                    <div className="ac-lessons-label">Lesson learned</div>
                    <div className="ac-lessons-body">
                      Controls that aren’t auditable are hard to defend. Governance requires traceability.
                    </div>
                  </div>
                </article>
              </div>

              <div className="ac-divider ac-divider--shimmer" aria-hidden="true" />
            </div>
          </section>

          <section className="ac-section ac-philosophy" id="philosophy">
            <div className="ac-container">
              <div className="ac-section-head">
                <h2 className="ac-h2">
                  Architecture Philosophy <span className="ac-h2-underline" aria-hidden="true" />
                </h2>
                <p className="ac-lede">
                  Ethosino’s philosophy is infrastructure-first: define boundaries, govern change, and make behavior
                  demonstrable. The design treats transparency as an operational requirement rather than a brand promise.
                </p>
              </div>

              <div className="ac-two-col ac-two-col--philosophy">
                <div className="ac-col">
                  <p className="ac-p">
                    Infrastructure products succeed when they remain consistent under pressure: growth, audits, integration churn,
                    and operational incidents. Ethosino’s architecture leans into separation of concerns to keep those pressures from
                    collapsing into a single fragile dependency. Tenant boundaries are enforced at the policy and data layers; governance
                    mechanisms keep operational change explicit; and verification artifacts ensure gameplay outcomes can be reconstructed
                    deterministically from recorded inputs.
                  </p>

                  <p className="ac-p">
                    A platform like this cannot depend on “trust.” It must produce durable evidence. That means decisions are modeled as
                    records: configuration changes, role assignments, policy updates, ruleset deployments, and outcome generation events.
                    Where possible, those records are structured to support export bundles that can be reviewed outside of the platform
                    without requiring privileged internal access.
                  </p>

                  <p className="ac-p">
                    Ethosino is built to be modular. Operators can adopt the platform as a cohesive deployment while still maintaining a path
                    toward incremental integration changes over time. Connector contracts and policy enforcement patterns are intended to
                    reduce lock-in, isolate risk, and keep the system’s behavior explainable as integrations evolve.
                  </p>

                  <div className="ac-code-card">
                    <div className="ac-code-head">
                      <div className="ac-code-title">Governed ruleset versioning concept</div>
                      <div className="ac-code-badges">
                        <span className="ac-chip">rulesetId</span>
                        <span className="ac-chip">version</span>
                        <span className="ac-chip">effectiveAt</span>
                      </div>
                    </div>
                    <pre className="ac-code">
    {`{
      "tenantId": "operator_7f2c",
      "rulesetId": "roulette_standard",
      "version": "2026.02.1",
      "effectiveAt": "2026-02-01T00:00:00Z",
      "changeReason": "Payout table formalized for audit export",
      "approvedByRole": "ComplianceAdmin",
      "hash": "b7f6...e2a9"
    }`}
                    </pre>
                    <div className="ac-code-foot">
                      Ruleset versioning is presented as a governance surface: changes are explicit, attributable,
                      and traceable over time.
                    </div>
                  </div>
                </div>

                <aside className="ac-col">
                  <div className="ac-panel ac-panel--principles">
                    <h3 className="ac-h3">Core Principles</h3>
                    <p className="ac-muted">
                      These principles guide the platform’s architecture decisions and product surfaces.
                    </p>

                    <ul className="ac-principles">
                      <li>
                        <span className="ac-pr-dot ac-pr-dot--cyan" aria-hidden="true" />
                        <div>
                          <div className="ac-pr-title">Deterministic behavior</div>
                          <div className="ac-pr-desc">
                            Outcomes and critical actions are modeled so they can be reconstructed from recorded inputs, not re-interpreted later.
                          </div>
                        </div>
                      </li>
                      <li>
                        <span className="ac-pr-dot ac-pr-dot--green" aria-hidden="true" />
                        <div>
                          <div className="ac-pr-title">Audit-first logging</div>
                          <div className="ac-pr-desc">
                            Logs are not an afterthought. The data model supports export bundles, reviewer comprehension, and dispute posture.
                          </div>
                        </div>
                      </li>
                      <li>
                        <span className="ac-pr-dot ac-pr-dot--violet" aria-hidden="true" />
                        <div>
                          <div className="ac-pr-title">Modular integration</div>
                          <div className="ac-pr-desc">
                            Integrations are approached as governed contracts with clear responsibilities rather than hidden dependencies.
                          </div>
                        </div>
                      </li>
                      <li>
                        <span className="ac-pr-dot ac-pr-dot--cyan" aria-hidden="true" />
                        <div>
                          <div className="ac-pr-title">Tenant isolation</div>
                          <div className="ac-pr-desc">
                            Multi-tenant boundaries are treated as an architectural property, with policy enforcement and scoped exports.
                          </div>
                        </div>
                      </li>
                      <li>
                        <span className="ac-pr-dot ac-pr-dot--green" aria-hidden="true" />
                        <div>
                          <div className="ac-pr-title">Versioned rule governance</div>
                          <div className="ac-pr-desc">
                            Rules evolve. The platform keeps changes explicit through versioning, attribution, and effective time windows.
                          </div>
                        </div>
                      </li>
                    </ul>

                    <div className="ac-panel-divider" />

                    <div className="ac-mini-stack">
                      <div className="ac-mini-stack-row">
                        <span className="ac-mini-k">Product posture</span>
                        <span className="ac-mini-v">Inspectability over appearance</span>
                      </div>
                      <div className="ac-mini-stack-row">
                        <span className="ac-mini-k">Operating posture</span>
                        <span className="ac-mini-v">Governed change + traceability</span>
                      </div>
                      <div className="ac-mini-stack-row">
                        <span className="ac-mini-k">Reviewer posture</span>
                        <span className="ac-mini-v">Exportable evidence bundles</span>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          <section className="ac-section ac-differentiation" id="difference">
            <div className="ac-container">
              <div className="ac-section-head">
                <h2 className="ac-h2">
                  What Makes <span className="mt-h2-accent">Ethosino Different</span> <span className="ac-h2-underline" aria-hidden="true" />
                </h2>
                <p className="ac-lede">
                  The platform is designed to reduce ambiguity. The objective is not to claim superiority, but to provide
                  a different operating posture: tenant-scoped governance and demonstrable system behavior.
                </p>
              </div>

              <div className="ac-compare">
                <div className="ac-compare-head">
                  <div className="ac-compare-title">Comparison: operational posture</div>
                  <div className="ac-compare-note">
                    These are general patterns observed in the market, not a critique of specific vendors.
                  </div>
                </div>

                <div className="ac-compare-grid" role="table" aria-label="Traditional White Label vs Ethosino Infrastructure comparison">
                  <div className="ac-compare-row ac-compare-row--head" role="row">
                    <div className="ac-compare-cell" role="columnheader">
                      Traditional white label
                    </div>
                    <div className="ac-compare-cell" role="columnheader">
                      Ethosino infrastructure
                    </div>
                  </div>

                  <div className="ac-compare-row" role="row">
                    <div className="ac-compare-cell" role="cell">
                      <div className="ac-compare-k">Outcome generation</div>
                      <div className="ac-compare-v">
                        Often treated as a provider guarantee. Operators may not receive deterministic artifacts or replay reconstruction tools.
                      </div>
                    </div>
                    <div className="ac-compare-cell" role="cell">
                      <div className="ac-compare-k">Outcome generation</div>
                      <div className="ac-compare-v">
                        Built around deterministic verification artifacts, ruleset identifiers, and replay reconstruction to support inspection and disputes.
                      </div>
                    </div>
                  </div>

                  <div className="ac-compare-row" role="row">
                    <div className="ac-compare-cell" role="cell">
                      <div className="ac-compare-k">Multi-operator boundary</div>
                      <div className="ac-compare-v">
                        Frequently configuration-level only. Tenant policies may not be enforceable across all system layers.
                      </div>
                    </div>
                    <div className="ac-compare-cell" role="cell">
                      <div className="ac-compare-k">Multi-operator boundary</div>
                      <div className="ac-compare-v">
                        Tenant identifiers, scoped policies, and access controls designed to enforce separation and allow tenant-scoped exports.
                      </div>
                    </div>
                  </div>

                  <div className="ac-compare-row" role="row">
                    <div className="ac-compare-cell" role="cell">
                      <div className="ac-compare-k">Operational change</div>
                      <div className="ac-compare-v">
                        Rule changes can be implicit, difficult to attribute, and hard to explain later when reviewers ask what changed and when.
                      </div>
                    </div>
                    <div className="ac-compare-cell" role="cell">
                      <div className="ac-compare-k">Operational change</div>
                      <div className="ac-compare-v">
                        Governed ruleset versioning and admin audit logs intended to keep change explicit, attributable, and reviewable.
                      </div>
                    </div>
                  </div>

                  <div className="ac-compare-row" role="row">
                    <div className="ac-compare-cell" role="cell">
                      <div className="ac-compare-k">Exports & evidence</div>
                      <div className="ac-compare-v">
                        Logs may exist, but evidence bundles can be inconsistent and not shaped for diligence or compliance advisory review.
                      </div>
                    </div>
                    <div className="ac-compare-cell" role="cell">
                      <div className="ac-compare-k">Exports & evidence</div>
                      <div className="ac-compare-v">
                        Export surfaces designed around audit-grade artifacts: deterministic verification records, ledger references, and traceable admin actions.
                      </div>
                    </div>
                  </div>

                  <div className="ac-compare-row" role="row">
                    <div className="ac-compare-cell" role="cell">
                      <div className="ac-compare-k">Provider dependency</div>
                      <div className="ac-compare-v">
                        A brand can be portable, but critical logic may remain locked behind third-party constraints with limited introspection.
                      </div>
                    </div>
                    <div className="ac-compare-cell" role="cell">
                      <div className="ac-compare-k">Provider dependency</div>
                      <div className="ac-compare-v">
                        Infrastructure posture prioritizes explainability and modularity, with integration contracts intended to reduce hidden coupling.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ac-compare-foot">
                  <div className="ac-compare-foot-left">
                    <span className="ac-badge">Enterprise posture</span>
                    <span className="ac-muted">
                      Designed for operator governance, investor diligence, and technical review workflows.
                    </span>
                  </div>
                  <a className="ac-inline-link" href="/solutions/startup-operators">
                    View operator solutions <span aria-hidden="true">→</span>
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section className="ac-section ac-founder" id="founder">
            <div className="ac-container">
              <div className="ac-section-head">
                <h2 className="ac-h2">
                  <span className="mt-h2-accent">The Founder</span> Perspective <span className="ac-h2-underline" aria-hidden="true" />
                </h2>
                <p className="ac-lede">
                  Ethosino is founder-led. The platform was built with a bias toward engineering discipline:
                  isolate boundaries, govern change, and make behavior demonstrable.
                </p>
              </div>

              <div className="ac-two-col ac-two-col--founder">
                <div className="ac-col">
                  <p className="ac-p">
                    Ethosino’s origins are founder-built, with an emphasis on full-stack system ownership: product surfaces,
                    data modeling, verification logic, and operator tooling. The objective was not to assemble a thin front-end
                    over hidden logic; it was to create infrastructure that can hold up under inspection and remain consistent as
                    operators scale. A founder-built posture also implies responsibility: the system’s behavior must be explained
                    clearly, not deferred to third-party vendors.
                  </p>

                  <p className="ac-p">
                    The technical mindset behind Ethosino is pragmatic. In infrastructure, there is no substitute for deterministic
                    behavior and traceability. A platform should be able to answer hard questions: how outcomes are derived, how tenant
                    boundaries are enforced, how rules evolve, and how operational changes are attributed. Those questions are not
                    theoretical — they show up during diligence, incidents, disputes, and internal controls.
                  </p>

                  <p className="ac-p">
                    This approach is less about “features” and more about posture. Ethosino tries to make the platform’s internal behavior
                    legible. That includes evidence artifacts, export surfaces, and an admin model that is structured around accountability.
                    The intention is not to replace legal, compliance, or security expertise — it is to provide a system that those teams can
                    review with clarity.
                  </p>

                  <blockquote className="ac-quote">
                    <div className="ac-quote-mark" aria-hidden="true">
                      “
                    </div>
                    <div className="ac-quote-body">
                      I built what I wanted to exist: infrastructure where the operator can demonstrate how the system behaves, not just
                      assert it. That requires deterministic verification, tenant-scoped governance, and exportable evidence — consistently,
                      across the product.
                    </div>
                    <div className="ac-quote-foot">
                      <span className="ac-quote-sig">Founder, Ethosino</span>
                      <span className="ac-quote-sep" aria-hidden="true" />
                      <a className="ac-inline-link" href="/book-demo">
                        Discuss the platform <span aria-hidden="true">→</span>
                      </a>
                    </div>
                  </blockquote>
                </div>

                <aside className="ac-col">
                  <div className="ac-panel ac-panel--founder">
                    <h3 className="ac-h3">Why infrastructure focus matters</h3>
                    <p className="ac-muted">
                      Operators rarely fail because they lack a brand. They fail because core systems collapse under growth,
                      scrutiny, or operational complexity.
                    </p>

                    <div className="ac-points">
                      <div className="ac-point">
                        <div className="ac-point-k">Diligence readiness</div>
                        <div className="ac-point-v">
                          Technical reviewers need clear artifacts and predictable behavior. “Trust us” does not scale.
                        </div>
                      </div>
                      <div className="ac-point">
                        <div className="ac-point-k">Governance</div>
                        <div className="ac-point-v">
                          Ruleset changes, permissions, and policy updates should be attributable and exportable.
                        </div>
                      </div>
                      <div className="ac-point">
                        <div className="ac-point-k">Dispute posture</div>
                        <div className="ac-point-v">
                          Replay reconstruction and deterministic verification reduce ambiguity when outcomes are questioned.
                        </div>
                      </div>
                      <div className="ac-point">
                        <div className="ac-point-k">Isolation</div>
                        <div className="ac-point-v">
                          Multi-tenant boundaries are essential when multiple operators share a platform deployment.
                        </div>
                      </div>
                    </div>

                    <div className="ac-panel-divider" />

                    <div className="ac-mini-metric">
                      <div className="ac-mini-metric-label">Core commitment</div>
                      <div className="ac-mini-metric-value">Make system behavior inspectable</div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          <section className="ac-section ac-maturity" id="maturity">
            <div className="ac-container">
              <div className="ac-section-head">
                <h2 className="ac-h2">
                  Platform <span className="mt-h2-accent">Maturity Today</span> <span className="ac-h2-underline" aria-hidden="true" />
                </h2>
                <p className="ac-lede">
                  The platform is structured as an operational environment rather than a prototype: tenant provisioning,
                  governance surfaces, verification workflows, and exportable records intended to support inspection.
                </p>
              </div>

              <div className="ac-maturity-grid">
                <article className="ac-layer">
                  <div className="ac-layer-top">
                    <div className="ac-layer-icon ac-layer-icon--cyan" aria-hidden="true" />
                    <h3 className="ac-h3">Application Layer</h3>
                  </div>
                  <p className="ac-p">
                    The application layer covers gameplay execution, operator-scoped catalog rules, and user-visible interfaces.
                    The focus is not merely on UI, but on consistent execution paths that can be linked to verification artifacts
                    and ledger references. A system is only operationally credible if it can explain outcomes in terms of recorded
                    inputs and governed rules.
                  </p>
                  <ul className="ac-bullets">
                    <li>Tenant-scoped game catalogs and access enforcement</li>
                    <li>Consistent round identifiers and outcome linkage</li>
                    <li>Ruleset identifiers used to anchor how outcomes were computed</li>
                  </ul>
                </article>

                <article className="ac-layer">
                  <div className="ac-layer-top">
                    <div className="ac-layer-icon ac-layer-icon--green" aria-hidden="true" />
                    <h3 className="ac-h3">Governance Layer</h3>
                  </div>
                  <p className="ac-p">
                    The governance layer is where multi-tenant control becomes enforceable: roles, permissions, policies, and audit logging.
                    This layer exists to ensure operators can run the platform with accountability. When a configuration changes, the platform
                    is designed to preserve what changed, when, and under which role or permission scope.
                  </p>
                  <ul className="ac-bullets">
                    <li>Role & permission matrices scoped per tenant</li>
                    <li>Admin action audit logging for accountability</li>
                    <li>Policy enforcement for limits, access, and ruleset gating</li>
                  </ul>
                </article>

                <article className="ac-layer">
                  <div className="ac-layer-top">
                    <div className="ac-layer-icon ac-layer-icon--violet" aria-hidden="true" />
                    <h3 className="ac-h3">Verification Layer</h3>
                  </div>
                  <p className="ac-p">
                    The verification layer connects fairness proof, deterministic replay, and exportable artifacts. The objective is to
                    reduce ambiguity: recorded inputs, commitment hashes, nonce strategies, and versioned rulesets are the building blocks
                    for repeatable verification. This layer supports dispute posture and technical diligence workflows.
                  </p>
                  <ul className="ac-bullets">
                    <li>Seed lifecycle records and commitment models</li>
                    <li>Replay artifacts shaped for deterministic reconstruction</li>
                    <li>Export bundles that group related evidence for review</li>
                  </ul>
                </article>
              </div>

              <div className="ac-callout ac-callout--export">
                <div className="ac-callout-title">Export posture</div>
                <div className="ac-callout-body">
                  In many systems, “exports” are raw logs. Ethosino’s approach treats exports as evidence bundles: linked records for
                  configuration, rulesets, outcomes, and ledger references, structured so reviewers can validate system behavior without
                  requiring privileged internal access.
                </div>
                <a className="ac-inline-link" href="/documentation">
                  View documentation <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </section>

          <section className="ac-section ac-vision" id="vision">
            <div className="ac-container">
              <div className="ac-section-head">
                <h2 className="ac-h2">
                  <span className="mt-h2-accent">Long-Term Vision</span> <span className="ac-h2-underline" aria-hidden="true" />
                </h2>
                <p className="ac-lede">
                  Ethosino’s long-term direction is conservative and infrastructure-centric: provide durable, governed operator tooling
                  that remains defensible under scrutiny and stable under growth.
                </p>
              </div>

              <div className="ac-two-col ac-two-col--vision">
                <div className="ac-col">
                  <p className="ac-p">
                    The platform is intended to support an infrastructure licensing model where operators adopt a tenant environment with
                    clear boundaries and governance surfaces. The emphasis is durability: predictable behavior, controlled change, and
                    evidence-grade exports. Infrastructure products earn trust by being legible and consistent over time, not by shipping
                    superficial features that obscure underlying risk.
                  </p>

                  <p className="ac-p">
                    Ethosino’s roadmap is centered on three durable themes. First: strengthen tenant-scoped policy controls so operators can
                    implement governance that matches their risk posture. Second: formalize integration contracts so that payment, identity,
                    and analytics integrations remain modular and reviewable rather than hidden dependencies. Third: deepen verification and
                    export tooling so that fairness and operational controls can be inspected through consistent, exportable artifacts.
                  </p>

                  <p className="ac-p">
                    The platform is built for collaboration with legal and compliance advisors, not as a replacement for them. When an operator
                    needs a conservative deployment posture — whether for investor review, partner diligence, or internal controls — the platform
                    aims to provide the mechanisms: logs, exports, versioning, and tenant boundaries. Operators and counsel define the policies;
                    Ethosino provides the enforcement surfaces and evidence trail.
                  </p>

                  <div className="ac-pill-row">
                    <span className="ac-pill">Infrastructure licensing model</span>
                    <span className="ac-pill">Tenant-scoped governance</span>
                    <span className="ac-pill">Modular connectors</span>
                    <span className="ac-pill">Evidence-grade exports</span>
                  </div>
                </div>

                <aside className="ac-col">
                  <div className="ac-panel ac-panel--vision">
                    <h3 className="ac-h3" style={{ paddingTop: 15, paddingLeft: 10 }}>What “durable” means here</h3>
                    <p className="ac-muted" style={{ paddingLeft: 12.25 }}>
                      Durability is not a slogan; it is a set of architectural commitments.
                    </p>

                    <ul className="ac-checks">
                      <li>
                        <span className="ac-check" aria-hidden="true">✓</span>
                        Rules change through governed versioning rather than silent edits.
                      </li>
                      <li>
                        <span className="ac-check" aria-hidden="true">✓</span>
                        Tenant boundaries are enforceable across policy and data surfaces.
                      </li>
                      <li>
                        <span className="ac-check" aria-hidden="true">✓</span>
                        Outcomes remain verifiable through deterministic replay artifacts.
                      </li>
                      <li>
                        <span className="ac-check" aria-hidden="true">✓</span>
                        Exports are shaped for review workflows, not just raw storage.
                      </li>
                      <li>
                        <span className="ac-check" aria-hidden="true">✓</span>
                        Integrations are modular contracts with explicit responsibilities.
                      </li>
                    </ul>

                    <div className="ac-panel-divider" />

                    <div className="ac-mini-metric">
                      <div className="ac-mini-metric-label">Review posture</div>
                      <div className="ac-mini-metric-value">
                        Built to withstand investor and technical diligence
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          <section className="ac-section ac-cta">
            <div className="ac-container">
              <div className="ac-cta-card">
                <div className="ac-cta-left">
                  <h2 className="ac-cta-title">
                    Built Deliberately. <span className="ac-accent">Engineered Transparently</span>.
                  </h2>
                  <div className="ac-cta-underline" aria-hidden="true" />
                  <p className="ac-cta-body">
                    If you are evaluating infrastructure for an operator environment, the best next step is to review
                    the platform through its actual behavior: tenant provisioning, governance controls, verification artifacts,
                    and export bundles. Ethosino is designed to be demonstrated and inspected.
                  </p>

                  <div className="ac-cta-actions">
                    <a className="ac-btn ac-btn--primary" href="/book-demo">
                      Book a Live Demo <span aria-hidden="true">→</span>
                    </a>
                    <a className="ac-btn ac-btn--ghost" href="/architecture.pdf">
                      Review Technical Architecture
                    </a>
                  </div>
                </div>

                <div className="ac-cta-right">
                  <div className="ac-trust">
                    <div className="ac-trust-item">
                      <span className="ac-trust-dot ac-trust-dot--cyan" aria-hidden="true" />
                      <div>
                        <div className="ac-trust-k">Tenant-scoped audit trails</div>
                        <div className="ac-trust-v">
                          Admin actions and policy changes are structured for export and review.
                        </div>
                      </div>
                    </div>
                    <div className="ac-trust-item">
                      <span className="ac-trust-dot ac-trust-dot--green" aria-hidden="true" />
                      <div>
                        <div className="ac-trust-k">Deterministic verification engine</div>
                        <div className="ac-trust-v">
                          Replay artifacts designed for deterministic outcome reconstruction.
                        </div>
                      </div>
                    </div>
                    <div className="ac-trust-item">
                      <span className="ac-trust-dot ac-trust-dot--violet" aria-hidden="true" />
                      <div>
                        <div className="ac-trust-k">Versioned rule governance</div>
                        <div className="ac-trust-v">
                          Rulesets evolve through versioning and attribution rather than silent edits.
                        </div>
                      </div>
                    </div>
                    <div className="ac-trust-item">
                      <span className="ac-trust-dot ac-trust-dot--cyan" aria-hidden="true" />
                      <div>
                        <div className="ac-trust-k">Modular operator provisioning</div>
                        <div className="ac-trust-v">
                          Tenant provisioning and policy surfaces designed to support multiple operator deployments.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="ac-cta-mini">
                    <div className="ac-cta-mini-k">Next step</div>
                    <div className="ac-cta-mini-v">
                      Request a technical walkthrough focused on controls, verification, and exports.
                    </div>
                  </div>
                </div>
              </div>

              <footer className="ac-page-foot">
                <span className="ac-muted">
                  Ethosino is infrastructure software. Operators remain responsible for legal compliance and deployment policy decisions.
                </span>
              </footer>
            </div>
          </section>
        </main>
      <Footer />
    </Fragment>
  );
};

export default AboutCompanyHelper;
